import { KnowledgeMarkdown } from '@/components/features/KnowledgeBase/KnowledgeMarkdown';
import RevisionHistoryModal from '@/components/features/KnowledgeBase/RevisionHistoryModal';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { cn } from '@/lib/utils';
import { type BreadcrumbItem, type PageProps } from '@/types';
import { Head, Link, router, usePage } from '@inertiajs/react';
import {
    AlignLeft,
    ArrowLeft,
    CalendarDays,
    ChevronRight,
    Clock,
    Edit,
    ExternalLink,
    FileText,
    Globe2,
    Lock,
    ThumbsDown,
    ThumbsUp,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { route } from 'ziggy-js';

interface KnowledgeCategory {
    id: string;
    name: string;
    slug: string;
}

interface KnowledgeArticle {
    id: string;
    category: KnowledgeCategory | null;
    locale: string;
    title: string;
    slug: string;
    summary: string | null;
    status: string;
    tags: string[];
    is_restricted: boolean;
    body_markdown: string;
    author: { id: string; name: string } | null;
    published_at: string | null;
    updated_at: string | null;
}

interface TranslationLink {
    id: string;
    locale: string;
    title: string;
    slug: string;
}

interface RelatedArticle {
    id: string;
    title: string;
    slug: string;
    locale: string;
}

interface PrevNextArticle {
    title: string;
    slug: string;
    locale: string;
}

interface HeadingLink {
    id: string;
    text: string;
    level: number;
}

interface KnowledgeBaseShowProps extends PageProps {
    article: KnowledgeArticle;
    article_translations: TranslationLink[];
    related_articles?: RelatedArticle[];
    prev_article?: PrevNextArticle | null;
    next_article?: PrevNextArticle | null;
    can_update: boolean;
    locale?: string;
    [key: string]: unknown;
}

function headingId(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
}

function extractHeadings(markdown: string): HeadingLink[] {
    return markdown
        .split('\n')
        .map((line) => {
            const match = /^(#{2,3})\s+(.+)$/.exec(line);
            if (!match) return null;
            return {
                id: headingId(match[2]),
                text: match[2].replace(/[*_`]/g, ''),
                level: match[1].length,
            };
        })
        .filter((h): h is HeadingLink => h !== null);
}

function estimateReadTime(markdown: string): number {
    const wordCount = markdown
        .replace(/```[\s\S]*?```/g, '')
        .replace(/[#*`_[\]()>-]/g, '')
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / 200));
}

function formatDate(date: string | null, locale: string): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString(locale === 'lt' ? 'lt-LT' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export default function KnowledgeBaseShowPage() {
    const { props } = usePage<KnowledgeBaseShowProps>();
    const {
        article,
        article_translations: articleTranslations,
        related_articles: relatedArticles,
        prev_article: prevArticle,
        next_article: nextArticle,
        can_update: canUpdate,
        locale = 'en',
    } = props;

    const { t } = useTranslations();
    const headings = useMemo(() => extractHeadings(article.body_markdown), [article.body_markdown]);
    const readTime = useMemo(() => estimateReadTime(article.body_markdown), [article.body_markdown]);
    const formattedUpdatedAt = formatDate(article.updated_at, locale);

    const [activeId, setActiveId] = useState<string | null>(headings[0]?.id ?? null);
    const [helpfulVote, setHelpfulVote] = useState<'yes' | 'no' | null>(null);

    const numberedHeadings = useMemo(() => {
        let count = 0;
        return headings.map((h) => ({ ...h, displayNumber: h.level === 2 ? ++count : null }));
    }, [headings]);

    useEffect(() => {
        if (headings.length === 0) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((e) => e.isIntersecting);
                if (visible.length > 0) setActiveId(visible[0].target.id);
            },
            { rootMargin: '0px 0px -70% 0px', threshold: 0 },
        );
        headings.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [headings]);

    const backHref = article.category ? `${route('knowledge-base.index')}?category=${article.category.slug}` : route('knowledge-base.index');
    const backLabel = article.category?.name ?? t('knowledge_base.index.breadcrumbs.knowledge_base', 'Knowledge Base');

    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('knowledge_base.index.breadcrumbs.knowledge_base', 'Knowledge Base'), href: '/knowledge-base' },
        ...(article.category ? [{ title: article.category.name, href: `${route('knowledge-base.index')}?category=${article.category.slug}` }] : []),
        { title: article.title, href: route('knowledge-base.show', { slug: article.slug, locale: article.locale }) },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={article.title} />

            <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>
                {/* Scrollable main content */}
                <div className="min-w-0 flex-1 overflow-y-auto">
                    <div className="mx-auto max-w-3xl px-6 py-8 sm:px-10">
                        {/* Back link */}
                        <Link
                            href={backHref}
                            className="text-muted-foreground hover:text-foreground mb-6 inline-flex items-center gap-1.5 text-sm transition-colors"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {t('knowledge_base.show.actions.back_to', 'Back to :name').replace(':name', backLabel)}
                        </Link>

                        {/* Status/restricted badges (non-published only) */}
                        {(article.status !== 'published' || article.is_restricted) && (
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                                {article.status !== 'published' && (
                                    <span className="bg-muted text-muted-foreground rounded px-2 py-0.5 text-xs font-medium">
                                        {t(`knowledge_base.status.${article.status}`, article.status)}
                                    </span>
                                )}
                                {article.is_restricted && (
                                    <span className="text-muted-foreground flex items-center gap-1 rounded border px-2 py-0.5 text-xs">
                                        <Lock className="h-3 w-3" />
                                        {t('knowledge_base.badge.restricted', 'Restricted')}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Title */}
                        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{article.title}</h1>

                        {/* Metadata row */}
                        <div className="text-muted-foreground mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                            {article.category && (
                                <>
                                    <Link
                                        href={`${route('knowledge-base.index')}?category=${article.category.slug}`}
                                        className="hover:text-foreground flex items-center gap-1.5 transition-colors"
                                    >
                                        <FileText className="h-3.5 w-3.5" />
                                        <span className="font-medium">{article.category.name}</span>
                                    </Link>
                                    <span>·</span>
                                </>
                            )}
                            <span className="flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5" />
                                {t('knowledge_base.updated_on', 'Last updated :date').replace(':date', formattedUpdatedAt)}
                            </span>
                            <span>·</span>
                            <span className="flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" />
                                {t('knowledge_base.show.read_time', ':min min read').replace(':min', String(readTime))}
                            </span>
                            {article.author && (
                                <>
                                    <span>·</span>
                                    <span>{article.author.name}</span>
                                </>
                            )}
                        </div>

                        {/* Summary */}
                        {article.summary && <p className="text-muted-foreground mt-4 text-base leading-relaxed">{article.summary}</p>}

                        {/* Divider */}
                        <div className="mt-6 border-b" />

                        {/* Article body */}
                        <div className="mt-8">
                            <KnowledgeMarkdown>{article.body_markdown}</KnowledgeMarkdown>
                        </div>

                        {/* Was this helpful? */}
                        <div className="mt-12 rounded-lg border p-4">
                            {helpfulVote ? (
                                <p className="text-muted-foreground text-sm">
                                    {t('knowledge_base.show.feedback.thank_you', 'Thanks for your feedback!')}
                                </p>
                            ) : (
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium">{t('knowledge_base.show.feedback.helpful', 'Was this helpful?')}</span>
                                        <button
                                            type="button"
                                            onClick={() => setHelpfulVote('yes')}
                                            className="hover:bg-accent flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors"
                                        >
                                            <ThumbsUp className="h-3.5 w-3.5" />
                                            {t('knowledge_base.show.feedback.yes', 'Yes')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setHelpfulVote('no')}
                                            className="hover:bg-accent flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors"
                                        >
                                            <ThumbsDown className="h-3.5 w-3.5" />
                                            {t('knowledge_base.show.feedback.no', 'No')}
                                        </button>
                                    </div>
                                    <span className="text-muted-foreground flex items-center gap-1 text-sm">
                                        {t('knowledge_base.show.feedback.have_feedback', 'Have feedback?')}{' '}
                                        <span className="text-primary font-medium">
                                            {t('knowledge_base.show.feedback.let_us_know', 'Let us know')}
                                        </span>
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Previous / Next navigation */}
                        {(prevArticle || nextArticle) && (
                            <div className="mt-6 grid grid-cols-2 gap-4 border-t pt-6">
                                {prevArticle ? (
                                    <Link
                                        href={route('knowledge-base.show', { slug: prevArticle.slug, locale: prevArticle.locale })}
                                        className="group hover:bg-accent/30 flex flex-col gap-1 rounded-lg border p-4 transition-colors"
                                    >
                                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                                            <ArrowLeft className="h-3.5 w-3.5" />
                                            {t('knowledge_base.show.navigation.previous', 'Previous article')}
                                        </span>
                                        <span className="group-hover:text-primary text-sm font-medium transition-colors">{prevArticle.title}</span>
                                    </Link>
                                ) : (
                                    <div />
                                )}
                                {nextArticle && (
                                    <Link
                                        href={route('knowledge-base.show', { slug: nextArticle.slug, locale: nextArticle.locale })}
                                        className="group hover:bg-accent/30 flex flex-col items-end gap-1 rounded-lg border p-4 transition-colors"
                                    >
                                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                                            {t('knowledge_base.show.navigation.next', 'Next article')}
                                            <ChevronRight className="h-3.5 w-3.5" />
                                        </span>
                                        <span className="group-hover:text-primary text-sm font-medium transition-colors">{nextArticle.title}</span>
                                    </Link>
                                )}
                            </div>
                        )}

                        <div className="h-12" />
                    </div>
                </div>

                {/* Right sidebar */}
                <aside className="hidden w-72 shrink-0 flex-col overflow-y-auto border-l xl:flex">
                    <div className="space-y-6 p-6">
                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                            {canUpdate && (
                                <>
                                    <Button variant="outline" size="sm" asChild>
                                        <Link href={route('knowledge-base.edit', article.id)}>
                                            <Edit className="h-3.5 w-3.5" />
                                            {t('knowledge_base.show.actions.edit', 'Edit')}
                                        </Link>
                                    </Button>
                                    <RevisionHistoryModal articleId={article.id} />
                                </>
                            )}
                            {articleTranslations.length > 1 && (
                                <div className="flex flex-wrap gap-1">
                                    {articleTranslations.map((tr) => (
                                        <button
                                            key={tr.id}
                                            type="button"
                                            onClick={() => router.visit(route('knowledge-base.show', { slug: tr.slug, locale: tr.locale }))}
                                            className={cn(
                                                'flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors',
                                                tr.locale === article.locale
                                                    ? 'bg-accent text-accent-foreground font-medium'
                                                    : 'text-muted-foreground hover:bg-accent',
                                            )}
                                        >
                                            <Globe2 className="h-3 w-3" />
                                            {tr.locale.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* On this page */}
                        {numberedHeadings.length > 0 && (
                            <div>
                                <div className="mb-3 flex items-center gap-2">
                                    <AlignLeft className="text-muted-foreground h-4 w-4" />
                                    <h3 className="text-sm font-semibold">{t('knowledge_base.show.sidebar.on_this_page', 'On this page')}</h3>
                                </div>
                                <nav className="space-y-0.5 border-l">
                                    {numberedHeadings.map((heading) => (
                                        <a
                                            key={heading.id}
                                            href={`#${heading.id}`}
                                            className={cn(
                                                'block border-l-2 border-transparent py-1 pr-2 text-sm leading-snug transition-colors',
                                                heading.level === 3 ? 'pl-6 text-xs' : 'pl-3',
                                                activeId === heading.id
                                                    ? 'border-primary text-primary font-medium'
                                                    : 'text-muted-foreground hover:border-muted-foreground hover:text-foreground',
                                            )}
                                        >
                                            {heading.displayNumber !== null ? `${heading.displayNumber}. ${heading.text}` : heading.text}
                                        </a>
                                    ))}
                                </nav>
                            </div>
                        )}

                        {/* Related articles */}
                        {relatedArticles && relatedArticles.length > 0 && (
                            <div>
                                <div className="mb-3 flex items-center gap-2">
                                    <FileText className="text-muted-foreground h-4 w-4" />
                                    <h3 className="text-sm font-semibold">{t('knowledge_base.show.sidebar.related', 'Related articles')}</h3>
                                </div>
                                <div className="space-y-1">
                                    {relatedArticles.map((ra) => (
                                        <Link
                                            key={ra.id}
                                            href={route('knowledge-base.show', { slug: ra.slug, locale: ra.locale })}
                                            className="group hover:bg-accent/30 flex items-center justify-between gap-2 rounded-md py-1.5 pr-1 transition-colors"
                                        >
                                            <span className="text-primary text-sm transition-colors">{ra.title}</span>
                                            <ChevronRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                        </Link>
                                    ))}
                                </div>
                                {article.category && (
                                    <Link
                                        href={`${route('knowledge-base.index')}?category=${article.category.slug}`}
                                        className="text-primary mt-3 block text-sm hover:underline"
                                    >
                                        {t('knowledge_base.show.sidebar.view_all_category', 'View all :name articles').replace(
                                            ':name',
                                            article.category.name,
                                        )}
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </aside>
            </div>
        </AppLayout>
    );
}
